import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  MenuItem,
  FormControl,
  Select,
  InputLabel
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import dashboardApiService from '../../services/dashboard/DashboardApiService';
import alertsApiService from '../../services/alerts/AlertsApiService';

const Overview = () => {
  const [agents, setAgents] = useState([]);
  const [statistics, setStatistics] = useState({
    totalAgents: 0,
    activeAgents: 0,
    inactiveAgents: 0,
    securityAlerts: 0
  });
  const [pieChartData, setPieChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [agentsData, alertsData] = await Promise.all([
        dashboardApiService.getAllAgents(),
        alertsApiService.getAllAlerts()
      ]);
      
      const stats = dashboardApiService.getAgentStatistics(agentsData);
      const chartData = dashboardApiService.getPieChartData(agentsData);
      
      setAgents(agentsData);
      setStatistics({
        ...stats,
        securityAlerts: alertsData.count || 0
      });
      setPieChartData(chartData);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleAgentSelect = (event) => {
    const agentId = event.target.value;
    setSelectedAgent(agentId);
    if (agentId !== 'all') {
      navigate(`/agents/${agentId}`);
    }
  };

  const formatLastKeepAlive = (lastKeepAlive) => {
    if (lastKeepAlive === '9999-12-31T23:59:59+00:00') {
      return 'Never disconnected';
    }
    return new Date(lastKeepAlive).toLocaleString();
  };

  const handleOverviewCardClick = (cardType) => {
    // Navigate to Agents page with appropriate filter
    switch (cardType) {
      case 'totalAgents':
        navigate('/agents?filter=all');
        break;
      case 'activeAgents':
        navigate('/agents?filter=active');
        break;
      case 'inactiveAgents':
        navigate('/agents?filter=inactive');
        break;
      case 'securityAlerts':
        navigate('/alerts');
        break;
      default:
        navigate('/agents');
    }
  };


  if (loading) {
    return (
      <Box className="overview-loading">
        <CircularProgress size={40} />
        <Typography variant="body1" className="loading-text">Loading dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="overview-error">
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchDashboardData}
          startIcon={<RefreshIcon />}
          className="retry-button"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="overview">
      <Box className="overview-header">
        <Typography variant="h4" className="overview-title">
          Security Overview
        </Typography>
        <Box className="overview-actions" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Select Agent</InputLabel>
            <Select
              value={selectedAgent}
              onChange={handleAgentSelect}
              label="Select Agent"
              size="small"
              sx={{
                backgroundColor: 'white',
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    All Agents Overview
                  </Typography>
                </Box>
              </MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                      <ComputerIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {agent.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {agent.id} • {agent.ip}
                      </Typography>
                    </Box>
                    <Chip
                      label={agent.status === 'active' ? 'Active' : 'Inactive'}
                      size="small"
                      color={agent.status === 'active' ? 'success' : 'error'}
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="contained" 
            onClick={fetchDashboardData}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>


      {/* Statistics Cards */}
      <Box className="stats-grid">
        <Card 
          className="stat-card total-agents"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('totalAgents')}
        >
          <CardContent>
            <Box className="stat-content">
              <PeopleIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Box className="stat-number">{statistics.totalAgents}</Box>
              <Typography variant="body2" className="stat-label">
                Total Agents
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card 
          className="stat-card active-agents"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('activeAgents')}
        >
          <CardContent>
            <Box className="stat-content">
              <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
              <Box className="stat-number">{statistics.activeAgents}</Box>
              <Typography variant="body2" className="stat-label">
                Active Agents
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card 
          className="stat-card security-alerts"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('securityAlerts')}
        >
          <CardContent>
            <Box className="stat-content">
              <WarningIcon sx={{ fontSize: 40, color: '#d32f2f', mb: 1 }} />
              <Box className="stat-number">{statistics.securityAlerts}</Box>
              <Typography variant="body2" className="stat-label">
                Security Alerts
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Card 
          className="stat-card disconnected"
          sx={{
            cursor: 'pointer',
            transform: 'scale(1)',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
            }
          }}
          onClick={() => handleOverviewCardClick('inactiveAgents')}
        >
          <CardContent>
            <Box className="stat-content">
              <WarningIcon sx={{ fontSize: 40, color: '#ed6c02', mb: 1 }} />
              <Box className="stat-number">{statistics.inactiveAgents}</Box>
              <Typography variant="body2" className="stat-label">
                Inactive Agents
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>


      {/* Charts Section */}
      <Box className="charts-section">
        <Card className="chart-card agent-status-chart">
          <CardContent>
            <Typography variant="h6" className="chart-title">
              Agent Status Distribution
            </Typography>
            <Box className="pie-chart-container">
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={140}
                      paddingAngle={3}
                      dataKey="value"
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={1000}
                      style={{ cursor: 'default' }}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.fill}
                          style={{ cursor: 'default' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      className="pie-chart-tooltip"
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={40}
                      iconType="circle"
                      className="pie-chart-legend"
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box className="chart-placeholder">
                  <Typography variant="body2" color="text.secondary">
                    No data available
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card className="chart-card recent-events">
          <CardContent>
            <Typography variant="h6" className="chart-title">
              Recent Security Events
            </Typography>
            <Box className="events-list">
              {agents.slice(0, 5).map((agent, index) => (
                <Box key={agent.id} className="event-item">
                  <Typography variant="body2" className="event-text">
                    {agent.status === 'active' ? 'Agent connected' : 'Agent disconnected'}: {agent.name}
                  </Typography>
                  <Typography variant="caption" className="event-time">
                    {formatLastKeepAlive(agent.lastKeepAlive)}
                  </Typography>
                </Box>
              ))}
              {agents.length === 0 && (
                <Box className="event-item">
                  <Typography variant="body2" className="event-text">
                    No recent events
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Overview;
